/**
 * Client-side check against the Have I Been Pwned "Pwned Passwords" API using
 * the k-anonymity model: we SHA-1 the password, send only the first 5 hex
 * chars, and scan the returned suffix list locally. The full password (and its
 * full hash) never leaves the browser.
 *
 * https://haveibeenpwned.com/API/v3#PwnedPasswords
 */
export type HibpCheckResult = {
  /** True when the password appears in a known breach corpus. */
  compromised: boolean;
  /** Number of times the password has been seen in breaches (0 when unknown). */
  count: number;
};

async function sha1Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-1", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

export async function checkPasswordPwned(
  password: string,
  opts?: { signal?: AbortSignal },
): Promise<HibpCheckResult> {
  if (!password || typeof crypto?.subtle?.digest !== "function") {
    return { compromised: false, count: 0 };
  }

  const hash = await sha1Hex(password);
  const prefix = hash.slice(0, 5);
  const suffix = hash.slice(5);

  const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
    method: "GET",
    // Ask HIBP to include additional padding so response size doesn't leak info.
    headers: { "Add-Padding": "true" },
    signal: opts?.signal,
  });

  if (!res.ok) {
    // Fail open — never block a legitimate login because HIBP is unreachable.
    return { compromised: false, count: 0 };
  }

  const body = await res.text();
  for (const line of body.split("\n")) {
    const [hashSuffix, countStr] = line.trim().split(":");
    if (!hashSuffix) continue;
    if (hashSuffix.toUpperCase() === suffix) {
      const count = Number.parseInt(countStr ?? "0", 10);
      // HIBP padding rows are returned with count 0; treat those as safe.
      if (!Number.isFinite(count) || count <= 0) {
        return { compromised: false, count: 0 };
      }
      return { compromised: true, count };
    }
  }

  return { compromised: false, count: 0 };
}

const STORAGE_KEY = "elara.mustResetPassword";

/**
 * Returns the user id currently marked as needing a forced password reset,
 * or `null` when no flag is stored. Exposed so callers can detect stale
 * entries left over from a previous session on the same tab.
 */
export function getStoredForcedResetUserId(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function readForcedResetFlag(userId: string | null | undefined): boolean {
  if (!userId) return false;
  return getStoredForcedResetUserId() === userId;
}

export function writeForcedResetFlag(userId: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, userId);
  } catch {
    /* storage disabled — ignore */
  }
}

export function clearForcedResetFlag(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

